import { Box } from '@mui/material';
import { RowTemplate } from '../layout/types';

/** Gap between panes in the glyph, in viewBox units. */
const GAP = 6;
const VIEW_BOX = 100;

/** Running start offset of each track, plus the total, in percent. */
function offsets(tracks: ReadonlyArray<number>): number[] {
    const result = [0];
    tracks.forEach((track) => result.push(result[result.length - 1] + track));
    return result;
}

interface LayoutGlyphProps {
    template: RowTemplate;
    /** Rendered edge length, px. */
    size?: number;
}

/**
 * Draws a row template as a small diagram: one rectangle per pane, placed and
 * sized by the template's own tracks. The picture is derived rather than
 * drawn, so a layout added to the menu illustrates itself and cannot show a
 * shape the grid would not hang.
 */
export default function LayoutGlyph({ template, size = 22 }: LayoutGlyphProps) {
    const columns = offsets(template.columnPercents);
    const rows = offsets(template.rowPercents);
    return (
        <Box
            component="svg"
            viewBox={`0 0 ${VIEW_BOX} ${VIEW_BOX}`}
            aria-hidden
            sx={{ width: size, height: size, flexShrink: 0 }}
        >
            {template.panes.map((pane) => {
                const { colStart, colSpan, rowStart, rowSpan } = pane.placement;
                const x = columns[colStart - 1];
                const y = rows[rowStart - 1];
                const width = columns[colStart - 1 + colSpan] - x;
                const height = rows[rowStart - 1 + rowSpan] - y;
                return (
                    // A plain rect: MUI's Box takes width and height as style
                    // props, so they would never reach the SVG attributes.
                    // Colour comes from the surrounding text through
                    // currentColor.
                    <rect
                        key={`${colStart}:${rowStart}`}
                        x={x + GAP / 2}
                        y={y + GAP / 2}
                        width={Math.max(0, width - GAP)}
                        height={Math.max(0, height - GAP)}
                        rx={3}
                        fill="currentColor"
                        fillOpacity={0.35}
                        stroke="currentColor"
                        strokeWidth={4}
                    />
                );
            })}
        </Box>
    );
}
